import { apiClient } from "@/http";
import { apiRoutes } from "./apiRoutes";
import type { Dataset, DatasetListQuery, DatasetSort } from "./dataset";
import type { PageResp } from "./page";

/**
 * 북마크 목록이다. 내가 담아 둔 공용 Dataset 참조를 가진다.
 *
 * 북마크는 자기 byte 를 가진 별도 엔티티가 아니다. **공용 Dataset 을 가리키는 per-member 목록**이다.
 * 항목은 공용 `Dataset` 과 같은 모양이다(flat). 그래서 목록 모델과 카드 모델을 재사용한다.
 *
 * 계약(backend `api/v1/me/bookmarks.py`)과 1:1 이다. 목록은 Dataset 목록과 같은 검색·라벨·facet
 * 필터 파라미터(`ListFilterParams` SoT)를 받는다. 서버는 담긴 범위 안에서 결과를 좁힌다.
 */

// 목록 정렬 축이다. Dataset 목록의 다섯 축에 담은 시각을 추가한다. 이 목록의 기본 정렬은
// 데이터셋 생성일이 아니라 담은 시각이다. 다른 축으로 정렬한 뒤 정렬을 해제하면 담은 시각으로 돌아온다.
export type BookmarkSort = DatasetSort | "bookmarked_at";

// 목록 필터 계약이다. Dataset 목록 쿼리에서 북마크에 없는 축만 뺀다:
//   - status: 계약상 available 만 표시한다.
//   - reviewed: 검토 축을 표시하지 않는다.
//   - operator: 담당자 축을 표시하지 않는다.
// 정렬 축만 넓힌다.
export type BookmarkListQuery = Omit<
  DatasetListQuery,
  "operator" | "status" | "reviewed" | "sort"
> & {
  sort?: BookmarkSort;
};

// 담기 payload 다. 대상 공용 Dataset 을 지정한다.
export type BookmarkCreateReq = {
  dataset_uuid: string;
};

// 목록에서 선택한 항목을 일괄로 담는 payload 다. 항목마다 호출하지 않는다(docs/rules.md §7).
export type BookmarkBulkCreateReq = {
  dataset_uuids: string[];
};

// 일괄 담기 결과다. 서버는 이미 담겨 있던 항목을 실패가 아니라 already_present 로 센다.
export type BookmarkBulkCreateResp = {
  added: number;
  already_present: number;
};

// 한 번에 담을 수 있는 상한이다. backend 검증과 같은 값이다(초과하면 422).
export const bookmarkBulkLimit = 100;

// 내가 담은 공용 Dataset page 를 조회한다(최근에 담은 순). 서버는 archived 원본을 목록과 total 에서 제외한다.
export async function listBookmarks(
  query: BookmarkListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<PageResp<Dataset>> {
  const response = await apiClient.get<PageResp<Dataset>>(apiRoutes.me.bookmarks, {
    params: query,
    signal: options.signal,
  });
  return response.data;
}

// 북마크에 담는다. 응답은 담긴 Dataset 자체다.
export async function addBookmark(datasetUuid: string): Promise<Dataset> {
  const response = await apiClient.post<Dataset>(apiRoutes.me.bookmarks, {
    dataset_uuid: datasetUuid,
  } satisfies BookmarkCreateReq);
  return response.data;
}

// 여러 공용 Dataset 을 한 요청으로 담는다. 하나라도 스코프 밖이면 서버는 전체를 404 로 거절한다.
// 부분 반영이 없으면 목록 선택 액션을 되돌리기가 단순하다.
export async function addBookmarksBulk(
  datasetUuids: readonly string[],
): Promise<BookmarkBulkCreateResp> {
  const response = await apiClient.post<BookmarkBulkCreateResp>(apiRoutes.me.bookmarksBulk, {
    dataset_uuids: [...datasetUuids],
  } satisfies BookmarkBulkCreateReq);
  return response.data;
}

// 북마크에서 뺀다(204). 공용 Dataset 은 바뀌지 않는다. 내 북마크에서만 사라진다.
export async function removeBookmark(datasetUuid: string): Promise<void> {
  await apiClient.delete(apiRoutes.me.bookmarkDetail(datasetUuid));
}
